import { DirectionalHint, TooltipDelay, TooltipHost } from "@fluentui/react";
import React, { useContext, useEffect, useState } from "react";
import Series from "../../../../components/SVGComponents/Series";
import More from "../../../../components/SVGComponents/Tools/More";
import { TOOLS, DROPDOWNTOOLS, MEASURE_TOOLS } from "../../../../constants/Constants";
import { ProbeTool } from "@cornerstonejs/tools";
// NEW: desktop Measure dropdown (measurement tools consolidated out of the toolbar + "..." menu)
import MeasureDropDown from "./MeasureDropDown";
import DownloadButton from "../Tools/ToolComponents/DownloadButton";
import LayoutSelect from "../Tools/ToolComponents/LayoutSelect";
import ShowOverlay from "../Tools/ToolComponents/ShowOverlay";
import Tool from "../Tools/ToolComponents/Tool";
import ToolDropDown from "./ToolDropDown";
import userContext from "../../../../context/users/UserContext";
import KeyImages from "../Tools/ToolComponents/KeyImage/KeyImages";
import HPTool from "../Tools/ToolComponents/HP/HPTool";
import ImageEnhancementTool from "../Tools/ToolComponents/ImageEnhancementTool";
import ColorLUTTool from "../Tools/ToolComponents/ColorLUTTool";
import { USERTYPES } from "../../../../constants/Constants";
import studyviewerContext from "../../../../context/studyviewer/StudyviewerContext";
import { useTranslation } from "react-i18next";
import ViewportContext from "../../../../context/studyviewer/ViewportContext";
import MPRToolbar from "../Tools/ToolComponents/MPRToolbar";
import { isMobile } from "react-device-detect";
import themeContext from "../../../../context/theme/ThemeContext";
import Exit from "../../../../components/SVGComponents/Tools/Exit";
import GetToolSVG from "../Tools/ToolComponents/GetToolSVG";
import { resizeViewport } from "../../helpers/CornerstoneViewportService";
import ThumbnailContext from "../../../../context/studyviewer/ThumbnailContext";
import {
  toolBarFlex,
  toolContainerStyles,
  toolTipStyles,
} from "../../styles/styles";
import studyReportContext from "../../../../context/studyreport/StudyReportContext";
import AnnotationContext from "../../../../context/annotation/AnnotationContext";
import CustomToolTipHost from "../../../../components/CustomToolTipHost/CustomToolTipHost";
import { formatBasicViewerUiTooltip } from "../../helpers/basicViewerToolShortcuts";

const TopToolbar = (props) => {
  const { roles, qcReviewer } = useContext(userContext);
  const { sharedViewer, isLandscape } = useContext(studyviewerContext);
  const { activeModality, activeStudyId, activeThumbnailIndex } = useContext(ThumbnailContext);
  const { isMPRActive, setIsMPRActive, isChildStudyActive, viewportDoubleClickIndex } = useContext(ViewportContext);
  const isFullScreenMode = viewportDoubleClickIndex !== -1 && viewportDoubleClickIndex != null;
  const { showKeyImagesAndTextAnnotation } = useContext(studyReportContext);
  const { theme } = useContext(themeContext);
  const { t } = useTranslation();

  const [hovered, setHovered] = useState(false);
  const handleMouseOver = () => {
    setHovered(true);
  };

  const handleMouseLeave = () => {
    setHovered(false);
  };

  const handleExitMPR = async () => {
    setIsMPRActive(false);
    setHovered(false);
    await resizeViewport();
  };

  useEffect(() =>{
    if(isMPRActive){
      handleExitMPR();
    }
  },[activeThumbnailIndex]);



  // Portrait non-mobile: single flat row with all tools distributed across full width
  if (!isLandscape && !isMobile) {
    return (
      <div
        style={{
          display: "flex",
          alignItems: "center",
          justifyContent: "flex-start",
          height: "100%",
        }}
      >
        {isMPRActive && (
          <div
            className="cursor-pointer"
            onClick={handleExitMPR}
            onMouseOver={handleMouseOver}
            onMouseLeave={handleMouseLeave}
            style={{
              display: "flex",
              flexDirection: "row",
              alignItems: "center",
              width: "80px",
              height: "100%",
              justifyContent: "space-between",
              margin: "0 10px",
            }}
          >
            <GetToolSVG toolName={"EXITMPR"} hovered={hovered} />
            <span style={{ fontSize: "14px", fontWeight: "400", lineHeight: "20px" }}>
              {t("viewer.tools.exit-mpr")}
            </span>
          </div>
        )}
        {!isFullScreenMode && (
          <div style={toolContainerStyles}>
            <CustomToolTipHost
              delay={TooltipDelay.zero}
              id="series-selector"
              directionalHint={DirectionalHint.bottomCenter}
              content={formatBasicViewerUiTooltip(
                props.seriesMenu ? t("viewer.tools.close-series") : t("viewer.tools.open-series"),
                "series"
              )}
              styles={toolTipStyles}
            >
              <div className="highlight-tool" onClick={props.openSeries} aria-describedby="series-selector">
                <Series color={props.seriesMenu ? theme.palette.activeTool : theme.palette.inactiveTool} />
              </div>
            </CustomToolTipHost>
          </div>
        )}
        {!isMPRActive && (
          <div style={{ ...toolContainerStyles, position: "relative" }}>
            <LayoutSelect />
          </div>
        )}
        <div style={toolContainerStyles}>
          <ShowOverlay />
        </div>
        {isMPRActive ? (
          <>
            <MPRToolbar />
            <div style={toolContainerStyles}>
              <ToolDropDown />
            </div>
          </>
        ) : (
          <>
            {TOOLS
              .filter((tool) => !MEASURE_TOOLS.some((m) => m.key === tool.key) && tool.key !== "FreeRotate")
              .map((tool, index) => {
                if (!tool.NotFor.includes(activeModality)) {
                  return (
                    <div key={index} style={toolBarFlex}>
                      <Tool tool={tool} />
                    </div>
                  );
                }
              })}
            {/* Measure dropdown — left of rotation */}
            <div style={toolContainerStyles}>
              <MeasureDropDown />
            </div>
            {/* Rotation kept to the RIGHT of Measure */}
            {TOOLS
              .filter((tool) => tool.key === "FreeRotate")
              .map((tool, index) => {
                if (!tool.NotFor.includes(activeModality)) {
                  return (
                    <div key={`rot_${index}`} style={toolBarFlex}>
                      <Tool tool={tool} />
                    </div>
                  );
                }
              })}
            <div>
              <HPTool />
            </div>
            {DROPDOWNTOOLS.slice(0, DROPDOWNTOOLS.findIndex(t => t.key === ProbeTool.toolName)).map((tool, index) => {
              if (!tool.NotFor.includes(activeModality)) {
                return (
                  <div key={`dd-${index}`} style={toolBarFlex}>
                    <Tool tool={tool} />
                  </div>
                );
              }
            })}
            {/* Image Enhancement top-level button — moved into the "..." menu (ToolDropDown).
            <div style={toolContainerStyles}>
              <ImageEnhancementTool />
            </div>
            */}
            {["CT", "MR", "PT"].includes(activeModality) && (
              <div style={toolContainerStyles}>
                <ColorLUTTool />
              </div>
            )}
            <div style={toolContainerStyles}>
              <ToolDropDown />
            </div>
          </>
        )}
      </div>
    );
  }

  return (
    <>
      <div
        style={{
          // borderRight: `${isMobile ? "" : "1.5px solid #3B3A39"}`,
          // paddingRight: "5px",
          // marginRight: `${isMobile ? "0" : "5px"}`,
          display: "flex",
          alignItems: "center",
          justifyContent: "space-evenly",
          height: "100%",
        }}
      >
        {/* Mobile uses "Close MPR" inside the MPR dropdown (MobileMprMenu),
            so the standalone Exit MPR button is hidden on mobile. Desktop
            (landscape) keeps it. */}
        {isMPRActive && !isMobile && (
          <div
            className="cursor-pointer"
            onClick={handleExitMPR}
            onMouseOver={handleMouseOver}
            onMouseLeave={handleMouseLeave}
            style={{
              display: "flex",
              flexDirection: "row",
              alignItems: "center",
              width: "80px",
              height: "100%",
              justifyContent: "space-between",
              margin: "0 10px",
            }}
          >
            <GetToolSVG toolName={"EXITMPR"} hovered={hovered} />
            <span
              style={{
                fontSize: "14px",
                fontWeight: "400",
                lineHeight: "20px",
              }}
            >
              {t("viewer.tools.exit-mpr")}
            </span>
          </div>
        )}
        {!isFullScreenMode && (
        <div
          style={{
            ...toolContainerStyles,
            marginRight: isMobile ? "0px !important" : "5px !important",
          }}
        >
          <div
            style={{
              display: "flex",
              alignItems: "center",
              height: "100%",
            }}
          >
            <CustomToolTipHost
              delay={TooltipDelay.zero}
              id="series-selector"
              directionalHint={DirectionalHint.bottomCenter}
              content={
                isMobile
                  ? ""
                  : formatBasicViewerUiTooltip(
                      props.seriesMenu
                        ? t("viewer.tools.close-series")
                        : t("viewer.tools.open-series"),
                      "series"
                    )
              }

              styles={toolTipStyles}
            >
              <div
                className={isMobile ? "highlight-tool-mobile" : "highlight-tool"}
                onClick={props.openSeries}
                aria-describedby="series-selector"
                style={{
                  height : isMobile ? "50px" : "30px",
                  flexDirection: isMobile ? "column" : "row"
                }}
              >
                <Series
                  color={
                    props.seriesMenu
                      ? theme.palette.activeTool
                      : theme.palette.inactiveTool
                  }
                />
                {isMobile && (
                  <span
                    style={{
                      fontSize: "9px",
                      lineHeight: "100%",
                      fontWeight: "400",
                      //color: "#FFFFFF",
                      //color: "var(--navbar-header-popup-color-txt)",
                      color: "var(--mobile-color-txt)",
                      marginTop:"5px"
                    }}
                  >
                    {t("viewer.overlay.series")}
                  </span>
                )}
              </div>
            </CustomToolTipHost>
          </div>
        </div>
        )}
        {!isMPRActive && !isMobile && (
          <div style={{...toolContainerStyles, position: "relative"}}>
            <LayoutSelect />
          </div>
        )}
        {!isMobile && <div style={toolContainerStyles}>
          <ShowOverlay />
        </div>}
      </div>
      {isMPRActive && !isMobile && isLandscape && (
        <>
          <MPRToolbar />
          <div style={toolContainerStyles}>
            <ToolDropDown />
          </div>
        </>
      )}
      {isMPRActive && !isMobile && !isLandscape && (
        <div style={toolContainerStyles}>
          <ToolDropDown />
        </div>
      )}
      {!isMPRActive && !isMobile && (
        <div
          className="cursor-pointer"
          style={{
            height: "100%",
            justifyContent: "space-evenly",
            ...toolBarFlex,
          }}
        >
          {/* NEW: measurement tools (Length/Bidirectional/EllipticalROI/RectangleROI) and
              rotation are handled separately below — measurement moved into <MeasureDropDown/>,
              which sits LEFT of the rotation tool. OLD: TOOLS.map rendered them all inline. */}
          {isLandscape && TOOLS
            .filter((tool) => !MEASURE_TOOLS.some((m) => m.key === tool.key) && tool.key !== "FreeRotate")
            .map((tool, index) => {
              if (!tool.NotFor.includes(activeModality)) {
                return (
                  <div key={index} style={toolBarFlex}>
                    <Tool tool={tool} />
                  </div>
                );
              }
            })}
          {/* NEW: Measure dropdown — left of rotation */}
          {isLandscape && (
            <div style={toolContainerStyles}>
              <MeasureDropDown />
            </div>
          )}
          {/* Rotation kept to the RIGHT of Measure */}
          {isLandscape && TOOLS
            .filter((tool) => tool.key === "FreeRotate")
            .map((tool, index) => {
              if (!tool.NotFor.includes(activeModality)) {
                return (
                  <div key={`rot_${index}`} style={toolBarFlex}>
                    <Tool tool={tool} />
                  </div>
                );
              }
            })}
          {isLandscape && showKeyImagesAndTextAnnotation && !isChildStudyActive && !sharedViewer && (
            <div>
            { /* <KeyImages /> */ }
            </div>
          )}


          {isLandscape && (
            <div style={toolContainerStyles}>
              <HPTool />
            </div>
          )}

          {/* Image Enhancement was a top-level toolbar button here. Moved into the
              three-dot ("...") menu (ToolDropDown) per request.
          <div style={toolContainerStyles}>
            <ImageEnhancementTool />
          </div>
          */}

          {["CT", "MR", "PT"].includes(activeModality) && (
            <div style={toolContainerStyles}>
              <ColorLUTTool />
            </div>
          )}

          <div style={toolContainerStyles}>
            <ToolDropDown />
          </div>
        </div>
      )}
    </>
  );
};

export default TopToolbar;
